/**
 * Document Upload Component with BD Expert Helper Resources
 *
 * Provides file upload functionality with contextual help, templates,
 * and best-practice links for each document type.
 *
 * Uses backend API for storage operations to avoid Supabase Auth issues.
 */

import { useState, useRef, useEffect } from 'react';
import {
  Upload,
  FileText,
  X,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Lightbulb,
  Download,
  Loader2,
  File,
  Trash2,
} from 'lucide-react';
import { supabase } from '../lib/supabase';

// API base URL (without /api - we add it explicitly in each endpoint)
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Helper to get auth token from Supabase session
const getAuthToken = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || null;
  } catch (err) {
    console.warn('Failed to get auth token:', err);
    return null;
  }
};

// Document type configurations with BD expert resources
const DOCUMENT_CONFIGS = {
  // Stage 3: Deal Ready Documents
  nda_template: {
    label: 'NDA Template',
    description: 'Mutual Non-Disclosure Agreement for confidential discussions',
    accept: '.pdf,.doc,.docx',
    maxSize: 5, // MB
    helpText: 'A bilateral (mutual) NDA is preferred by most pharma BD teams.',
    resources: [
      {
        label: 'Cooley GO Mutual NDA Template (Free)',
        url: 'https://www.cooleygo.com/documents/form-bilateral-nda/',
        description: 'Silicon Valley top law firm free template',
      },
      {
        label: 'Understanding NDA Types',
        url: 'https://www.lawinsider.com/clause/mutual-nda',
        description: 'Unilateral vs Bilateral NDA explained',
      },
    ],
    tips: [
      'Use bilateral (mutual) NDA - shows professionalism',
      'Standard term: 3-5 years confidentiality period',
      'Include carve-outs for independently developed info',
    ],
  },
  corporate_deck: {
    label: 'Corporate Presentation',
    description: '10-15 page company overview for initial BD screening',
    accept: '.pdf,.ppt,.pptx',
    maxSize: 20, // MB
    helpText: 'This is your primary pitch document. Make it count.',
    resources: [
      {
        label: 'YC Biotech Pitch Deck Guide',
        url: 'https://www.ycombinator.com/library/4T-how-to-design-a-better-pitch-deck',
        description: 'Best practices from Y Combinator',
      },
      {
        label: 'SEC EDGAR - IPO S-1 Filings',
        url: 'https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&type=S-1',
        description: 'See real biotech pitch decks from IPO filings',
      },
    ],
    tips: [
      'Executive Summary (1 page)',
      'Unmet Medical Need (1-2 pages)',
      'Pipeline Overview (2-3 pages)',
      'Clinical Data Highlights (2-3 pages)',
      'Competitive Landscape (1 page)',
      'Team & Advisors (1 page)',
      'Partnering Objectives (1 page)',
    ],
    suggestedOutline: true,
  },
  teaser: {
    label: 'Non-Confidential Teaser',
    description: '1-2 page summary that can be shared before NDA signing',
    accept: '.pdf',
    maxSize: 5, // MB
    helpText: 'A teaser allows buyers to assess fit before committing to NDA.',
    resources: [
      {
        label: 'BIO Partnering Guidelines',
        url: 'https://www.bio.org/events/bio-partnering',
        description: 'Industry standard partnering formats',
      },
    ],
    tips: [
      'No confidential clinical data',
      'High-level pipeline overview only',
      'Clear contact information',
      'Brief company background',
    ],
    whatToInclude: [
      'Company name and overview',
      'Therapeutic focus areas',
      'Pipeline stage summary (no detailed data)',
      'Partnership interests',
      'Contact information',
    ],
    whatToAvoid: [
      'Unpublished clinical data',
      'Proprietary mechanisms',
      'Detailed IP information',
      'Financial projections',
    ],
  },

  // Stage 4: DD Documents (Premium)
  investigator_brochure: {
    label: "Investigator's Brochure (IB)",
    description: 'Core clinical trial document required for due diligence',
    accept: '.pdf',
    maxSize: 50, // MB
    helpText: 'The IB is the most important clinical document for DD.',
    resources: [
      {
        label: 'FDA IB Guidance',
        url: 'https://www.fda.gov/media/146167/download',
        description: 'Official FDA guidance document',
      },
      {
        label: 'ICH E6(R2) GCP Guidelines',
        url: 'https://database.ich.org/sites/default/files/E6_R2_Addendum.pdf',
        description: 'International standards for IB content',
      },
    ],
    tips: [
      'Keep updated with latest clinical data',
      'Include all safety information',
      'Follow ICH E6 format requirements',
    ],
  },
  clinical_data_package: {
    label: 'Clinical Data Package',
    description: 'CSR, topline data, or clinical study summaries',
    accept: '.pdf,.zip',
    maxSize: 100, // MB
    helpText: 'Clinical data is the most scrutinized part of DD.',
    resources: [
      {
        label: 'ICH E3 Clinical Study Report Format',
        url: 'https://database.ich.org/sites/default/files/E3_Guideline.pdf',
        description: 'Standard CSR structure and content',
      },
    ],
    tips: [
      'Include statistical analysis plan',
      'Provide raw data if requested',
      'Highlight key efficacy endpoints',
      'Document all AEs and SAEs',
    ],
  },
  patent_summary: {
    label: 'Patent Portfolio Summary',
    description: 'IP protection scope, expiry dates, FTO analysis',
    accept: '.pdf,.xlsx,.xls',
    maxSize: 10, // MB
    helpText: 'IP is critical for deal valuation. Be comprehensive.',
    tips: [
      'List all granted and pending patents',
      'Include expiry dates and extensions',
      'Note key claims and coverage',
      'Mention FTO status if available',
    ],
  },
  cmc_overview: {
    label: 'CMC Overview',
    description: 'Manufacturing process, supply chain, capacity info',
    accept: '.pdf',
    maxSize: 20, // MB
    helpText: 'Buyers need to understand manufacturing readiness.',
    resources: [
      {
        label: 'FDA CMC Guidance',
        url: 'https://www.fda.gov/regulatory-information/search-fda-guidance-documents',
        description: 'Search for CMC-related guidance documents',
      },
    ],
    tips: [
      'Describe manufacturing process',
      'List key suppliers and CDMOs',
      'Include capacity and scalability info',
      'Note any supply chain risks',
    ],
  },
  regulatory_status: {
    label: 'Regulatory Filing Status',
    description: 'IND/CTA approvals, regulatory correspondence',
    accept: '.pdf,.zip',
    maxSize: 30, // MB
    helpText: 'Regulatory history shows development maturity.',
    resources: [
      {
        label: 'FDA IND Application Guide',
        url: 'https://www.fda.gov/drugs/types-applications/investigational-new-drug-ind-application',
        description: 'IND requirements and process',
      },
    ],
    tips: [
      'Include IND/CTA approval letters',
      'List all regulatory interactions',
      'Note any clinical holds or concerns',
      'Document breakthrough/fast track status',
    ],
  },
};

// Helper component for resource links
function ResourceLink({ resource }) {
  return (
    <a
      href={resource.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-start gap-2 p-2 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors group"
    >
      <ExternalLink className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium text-blue-700 group-hover:underline">
          {resource.label}
        </span>
        {resource.description && (
          <p className="text-xs text-blue-600 mt-0.5">{resource.description}</p>
        )}
      </div>
    </a>
  );
}

// Helper component for tips list
function TipsList({ tips, title = 'Tips' }) {
  return (
    <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
      <div className="flex items-center gap-2 mb-2">
        <Lightbulb className="w-4 h-4 text-amber-600" />
        <span className="text-sm font-medium text-amber-800">{title}</span>
      </div>
      <ul className="space-y-1">
        {tips.map((tip, index) => (
          <li key={index} className="text-xs text-amber-700 flex items-start gap-2">
            <span className="text-amber-500 mt-0.5">•</span>
            <span>{tip}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Main Document Upload Component
export function DocumentUpload({
  documentType,
  sellerId,
  currentFile,
  onUploadComplete,
  onDelete,
  showHelp = true,
  compact = false,
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [showResources, setShowResources] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const fileInputRef = useRef(null);

  const config = DOCUMENT_CONFIGS[documentType];
  if (!config) {
    console.error(`Unknown document type: ${documentType}`);
    return null;
  }

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size
    const maxSizeBytes = config.maxSize * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      setError(`File too large. Maximum size is ${config.maxSize}MB.`);
      return;
    }

    // Validate file type
    const extension = '.' + file.name.split('.').pop().toLowerCase();
    const acceptedTypes = config.accept.split(',');
    if (!acceptedTypes.includes(extension)) {
      setError(`Invalid file type. Accepted: ${config.accept}`);
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('documentType', documentType);

      // Upload via backend API (avoids Supabase Auth issues)
      const response = await fetch(`${API_URL}/api/storage/sellers/${sellerId}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to upload file');
      }

      const result = await response.json();

      onUploadComplete?.({
        type: documentType,
        fileName: result.data.fileName,
        filePath: result.data.filePath,
        fileSize: result.data.fileSize,
        uploadedAt: result.data.uploadedAt,
      });
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async () => {
    if (!currentFile?.filePath) return;

    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      // Delete via backend API (avoids Supabase Auth issues)
      const response = await fetch(`${API_URL}/api/storage/sellers/${sellerId}/file`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ filePath: currentFile.filePath }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.warn('Storage delete warning:', errorData.message);
      }
    } catch (err) {
      console.warn('Storage delete failed:', err.message);
    }

    // Update UI/database regardless of storage deletion result
    onDelete?.(documentType);
  };

  const handleDownload = async () => {
    if (!currentFile?.filePath) return;

    setDownloading(true);
    setError(null);

    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      // Get signed URL via backend API (avoids Supabase Auth issues)
      const params = new URLSearchParams({
        filePath: currentFile.filePath,
        fileName: currentFile.fileName || 'download'
      });
      const response = await fetch(
        `${API_URL}/api/storage/signed-url?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to get download URL');
      }

      const result = await response.json();

      // Fetch the file as blob for reliable download
      const fileResponse = await fetch(result.data.signedUrl);
      if (!fileResponse.ok) {
        throw new Error('Failed to download file');
      }

      const blob = await fileResponse.blob();
      const blobUrl = URL.createObjectURL(blob);

      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = currentFile.fileName || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    } catch (err) {
      console.error('Download error:', err);
      setError('Failed to download file. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  // Compact view (for embedded in other forms)
  if (compact) {
    return (
      <div className="space-y-2">
        <label className="label flex items-center gap-2">
          <FileText className="w-4 h-4 text-gray-400" />
          {config.label}
        </label>

        {currentFile ? (
          <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg border border-green-200">
            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
            <span className="text-sm text-green-700 truncate flex-1">
              {currentFile.fileName}
            </span>
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="p-1 hover:bg-green-100 rounded disabled:opacity-50"
              title="Download file"
            >
              {downloading ? (
                <Loader2 className="w-3 h-3 animate-spin text-green-600" />
              ) : (
                <Download className="w-4 h-4 text-green-600" />
              )}
            </button>
            <button
              onClick={handleDelete}
              className="p-1 hover:bg-green-100 rounded"
              title="Remove file"
            >
              <Trash2 className="w-4 h-4 text-green-600" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <label className="flex-1 flex items-center justify-center gap-2 p-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept={config.accept}
                onChange={handleFileSelect}
                className="hidden"
                disabled={uploading}
              />
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin text-purple-500" />
              ) : (
                <Upload className="w-4 h-4 text-gray-400" />
              )}
              <span className="text-sm text-gray-600">
                {uploading ? 'Uploading...' : 'Click to upload'}
              </span>
            </label>
          </div>
        )}

        {error && (
          <p className="text-xs text-red-600 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            {error}
          </p>
        )}

        {showHelp && config.resources && (
          <p className="text-xs text-gray-500">
            Need a template?{' '}
            <a
              href={config.resources[0]?.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-600 hover:underline"
            >
              Get free template →
            </a>
          </p>
        )}
      </div>
    );
  }

  // Full view with all helper content
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="p-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900">{config.label}</h4>
              <p className="text-sm text-gray-500">{config.description}</p>
            </div>
          </div>
          {currentFile && (
            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              Uploaded
            </span>
          )}
        </div>
      </div>

      {/* Upload Area */}
      <div className="p-4">
        {currentFile ? (
          <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
            <File className="w-8 h-8 text-green-600" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-green-800 truncate">
                {currentFile.fileName}
              </p>
              <p className="text-xs text-green-600">
                Uploaded {new Date(currentFile.uploadedAt).toLocaleDateString()}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {currentFile.filePath && (
                <button
                  onClick={handleDownload}
                  disabled={downloading}
                  className="p-2 hover:bg-green-100 rounded-lg disabled:opacity-50"
                  title="Download"
                >
                  {downloading ? (
                    <Loader2 className="w-4 h-4 animate-spin text-green-600" />
                  ) : (
                    <Download className="w-4 h-4 text-green-600" />
                  )}
                </button>
              )}
              <button
                onClick={handleDelete}
                className="p-2 hover:bg-red-100 rounded-lg"
                title="Delete"
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </button>
            </div>
          </div>
        ) : (
          <label className="block p-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-colors text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept={config.accept}
              onChange={handleFileSelect}
              className="hidden"
              disabled={uploading}
            />
            {uploading ? (
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
                <span className="text-sm text-purple-600">Uploading...</span>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Upload className="w-8 h-8 text-gray-400" />
                <span className="text-sm text-gray-600">
                  Click to upload or drag and drop
                </span>
                <span className="text-xs text-gray-400">
                  {config.accept.replace(/\./g, '').toUpperCase()} • Max {config.maxSize}MB
                </span>
              </div>
            )}
          </label>
        )}

        {error && (
          <div className="mt-3 p-3 bg-red-50 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
            <button
              onClick={() => setError(null)}
              className="ml-auto p-1 hover:bg-red-100 rounded"
            >
              <X className="w-4 h-4 text-red-500" />
            </button>
          </div>
        )}
      </div>

      {/* Help Section */}
      {showHelp && (
        <div className="px-4 pb-4">
          {/* Help Text */}
          {config.helpText && (
            <p className="text-sm text-gray-600 mb-3">
              <Lightbulb className="w-4 h-4 inline mr-1 text-amber-500" />
              {config.helpText}
            </p>
          )}

          {/* Toggle Resources */}
          <button
            onClick={() => setShowResources(!showResources)}
            className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
          >
            {showResources ? 'Hide' : 'Show'} templates & resources
            <ExternalLink className="w-3 h-3" />
          </button>

          {showResources && (
            <div className="mt-3 space-y-3">
              {/* Resources Links */}
              {config.resources && config.resources.length > 0 && (
                <div className="space-y-2">
                  <span className="text-xs font-medium text-gray-500 uppercase">
                    Templates & References
                  </span>
                  {config.resources.map((resource, index) => (
                    <ResourceLink key={index} resource={resource} />
                  ))}
                </div>
              )}

              {/* Tips */}
              {config.tips && (
                <TipsList
                  tips={config.tips}
                  title={config.suggestedOutline ? 'Suggested Outline' : 'Best Practices'}
                />
              )}

              {/* What to Include/Avoid (for teaser) */}
              {config.whatToInclude && (
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <span className="text-xs font-medium text-green-700 uppercase flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Include
                    </span>
                    <ul className="mt-2 space-y-1">
                      {config.whatToInclude.map((item, index) => (
                        <li key={index} className="text-xs text-green-700">
                          • {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                    <span className="text-xs font-medium text-red-700 uppercase flex items-center gap-1">
                      <X className="w-3 h-3" /> Avoid
                    </span>
                    <ul className="mt-2 space-y-1">
                      {config.whatToAvoid.map((item, index) => (
                        <li key={index} className="text-xs text-red-700">
                          • {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Document Upload Section for Company Profile
export function DocumentUploadSection({ sellerId, documents: initialDocuments = {}, onUploadComplete }) {
  const [documents, setDocuments] = useState(initialDocuments);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Update local state when props change
  useEffect(() => {
    setDocuments(initialDocuments);
  }, [initialDocuments]);

  const saveDocumentsToServer = async (newDocs) => {
    setSaving(true);
    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }
      const response = await fetch(`${API_URL}/api/sellers/${sellerId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ documents: newDocs }),
      });

      if (!response.ok) {
        throw new Error('Failed to save documents');
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      onUploadComplete?.();
    } catch (err) {
      console.error('Error saving documents:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleUploadComplete = (fileInfo) => {
    const newDocs = {
      ...documents,
      [fileInfo.type]: fileInfo,
    };
    setDocuments(newDocs);
    saveDocumentsToServer(newDocs);
  };

  const handleDelete = (documentType) => {
    const newDocs = { ...documents };
    delete newDocs[documentType];
    setDocuments(newDocs);
    saveDocumentsToServer(newDocs);
  };

  return (
    <div className="space-y-4">
      {/* Success/Saving indicator */}
      {saving && (
        <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
          <span className="text-sm text-blue-700">Saving document...</span>
        </div>
      )}
      {saveSuccess && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle className="w-4 h-4 text-green-500" />
          <span className="text-sm text-green-700">Document saved successfully!</span>
        </div>
      )}

      {/* Stage 3: Deal Ready Documents */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          Deal Ready Documents
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Essential documents for partnership discussions. These can be shared with interested buyers.
        </p>

        <div className="space-y-4">
          <DocumentUpload
            documentType="nda_template"
            sellerId={sellerId}
            currentFile={documents.nda_template}
            onUploadComplete={handleUploadComplete}
            onDelete={handleDelete}
          />
          <DocumentUpload
            documentType="corporate_deck"
            sellerId={sellerId}
            currentFile={documents.corporate_deck}
            onUploadComplete={handleUploadComplete}
            onDelete={handleDelete}
          />
          <DocumentUpload
            documentType="teaser"
            sellerId={sellerId}
            currentFile={documents.teaser}
            onUploadComplete={handleUploadComplete}
            onDelete={handleDelete}
          />
        </div>
      </div>

      {/* Stage 4: DD Documents */}
      <div className="pt-6 border-t border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          Due Diligence Documents
          <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded">
            Premium
          </span>
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Prepare these documents ahead of time to accelerate due diligence when you find the right partner.
        </p>

        <div className="space-y-4">
          <DocumentUpload
            documentType="investigator_brochure"
            sellerId={sellerId}
            currentFile={documents.investigator_brochure}
            onUploadComplete={handleUploadComplete}
            onDelete={handleDelete}
          />
          <DocumentUpload
            documentType="clinical_data_package"
            sellerId={sellerId}
            currentFile={documents.clinical_data_package}
            onUploadComplete={handleUploadComplete}
            onDelete={handleDelete}
          />
          <DocumentUpload
            documentType="patent_summary"
            sellerId={sellerId}
            currentFile={documents.patent_summary}
            onUploadComplete={handleUploadComplete}
            onDelete={handleDelete}
          />
          <DocumentUpload
            documentType="cmc_overview"
            sellerId={sellerId}
            currentFile={documents.cmc_overview}
            onUploadComplete={handleUploadComplete}
            onDelete={handleDelete}
          />
          <DocumentUpload
            documentType="regulatory_status"
            sellerId={sellerId}
            currentFile={documents.regulatory_status}
            onUploadComplete={handleUploadComplete}
            onDelete={handleDelete}
          />
        </div>
      </div>
    </div>
  );
}

export default DocumentUpload;
